import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import PropertyCard from "./propertycard";
import axios from 'axios';
import PropertyLoader from './propertyLoader'

const Carousel = React.memo(({ properties, onLoadMore, hasMore, isLoading, currentPage }) => {
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);
    const carouselRef = useRef(null);
    const [thumbnailsCache, setThumbnailsCache] = useState({});
    const scrollCheckTimeout = useRef(null);
    const loadMoreTriggered = useRef(false);

    // Memoize properties to prevent unnecessary changes
    const memoizedProperties = useMemo(() => properties, [properties]);

    // Updated scroll position check
    const checkScrollPosition = useCallback(() => {
        if (!carouselRef.current) return;

        const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;
        const tolerance = 5;

        setIsAtStart(scrollLeft <= tolerance);
        
        // Only disable right button if we're at the physical end AND no more data
        const isAtPhysicalEnd = scrollLeft + clientWidth >= scrollWidth - tolerance;
        setIsAtEnd(isAtPhysicalEnd && !hasMore);
    }, [hasMore]);

    // Throttled scroll handler
    const handleScroll = useCallback(() => {
        if (scrollCheckTimeout.current) {
            clearTimeout(scrollCheckTimeout.current);
        }

        scrollCheckTimeout.current = setTimeout(() => {
            checkScrollPosition();

            if (!carouselRef.current) return;
            const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;

            // Trigger load more when near the end
            if (scrollLeft + clientWidth >= scrollWidth - 200 && hasMore && !isLoading && !loadMoreTriggered.current) {
                loadMoreTriggered.current = true;
                onLoadMore();
            }
        }, 100);
    }, [hasMore, isLoading, onLoadMore, checkScrollPosition]);

    // Reset load more trigger when loading completes
    useEffect(() => {
        if (!isLoading) {
            loadMoreTriggered.current = false;
        }
    }, [isLoading]);

    // Update scroll position check when hasMore or properties change
    useEffect(() => {
        checkScrollPosition();
    }, [hasMore, memoizedProperties, checkScrollPosition]);

    // Fetch thumbnails
    const fetchThumbnails = useCallback(async (newProps) => {
        const newIds = newProps.filter(p => !thumbnailsCache[p.id]).map(p => p.id);
        if (newIds.length === 0) return;

        try {
            const requests = newIds.map(id => {
                // const url = `${process.env.REACT_APP_API_URL}api/Images/property/${id}/thumbnail?width=300&height=192&quality=80`;
                 const url = `${process.env.REACT_APP_API_URL}api/Properties/${id}/thumbnail?width=300&height=192&quality=80`;
                return axios.get(url, { responseType: 'blob' })
                    .then(res => ({ id, thumbnailUrl: URL.createObjectURL(res.data) }))
                    .catch(() => ({ id, thumbnailUrl: null }));
            });

            const results = await Promise.all(requests);
            const newThumbs = results.reduce((acc, { id, thumbnailUrl }) => {
                acc[id] = thumbnailUrl;
                return acc;
            }, {});

            setThumbnailsCache(prev => ({ ...prev, ...newThumbs }));
        } catch (error) {
            console.error("Thumbnail fetch error:", error);
        }
    }, [thumbnailsCache]);

    // Set up event listeners and initial state
    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        carousel.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);

        return () => {
            carousel.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
            if (scrollCheckTimeout.current) {
                clearTimeout(scrollCheckTimeout.current);
            }
        };
    }, [handleScroll]);

    // Fetch thumbnails when properties change
    useEffect(() => {
        if (memoizedProperties.length > 0) {
            fetchThumbnails(memoizedProperties);
        }
    }, [memoizedProperties, fetchThumbnails]);

    // Scroll functions
    const scrollLeft = useCallback(() => {
        carouselRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
    }, []);

    const scrollRight = useCallback(() => {
        if (!carouselRef.current) return;

        const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;
        const isAtPhysicalEnd = scrollLeft + clientWidth >= scrollWidth - 50;

        // If we're at the physical end and have more data, load more
        if (isAtPhysicalEnd && hasMore && !isLoading) {
            onLoadMore();
        } else {
            // Otherwise just scroll
            carouselRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
    }, [hasMore, isLoading, onLoadMore]);

    // Memoize the rendered cards
    const renderedCards = useMemo(() => {
        if (memoizedProperties.length === 0 && !isLoading) {
            return <span className="loader"></span>;
        }

        return (
            <>
                {memoizedProperties.map((item) => (
                    <PropertyCard
                        item={item}
                        key={item.id}
                        thumbNails={[{ id: item.id, thumbnailUrl: thumbnailsCache[item.id] || null }]}
                    />
                ))}
                {isLoading && (
                    <div className="d-flex justify-content-center align-items-center">
                        <PropertyLoader />
                    </div>
                )}
            </>
        );
    }, [memoizedProperties, isLoading, thumbnailsCache]);

    return (
        <div>
            <div className={`col-12 mt-md-3 mt-2`}>
                <button
                    className={`carousel-button start-0 ${isAtStart ? 'disabled' : ''}`}
                    onClick={scrollLeft}
                    disabled={isAtStart}
                >
                    <span style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>‹</span>
                </button>

                <div className={"property-carousel-wrapper"} ref={carouselRef}>
                    {renderedCards}
                </div>

                <button
                    className={`carousel-button end-0 ${isAtEnd && !isLoading ? 'disabled' : ''}`}
                    onClick={scrollRight}
                    disabled={isAtEnd && !isLoading}
                >
                    <span style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>›</span>
                </button>
            </div>
        </div>
    )
});

export default Carousel;